/**
 * 대시보드 스타일 계산 유틸리티 함수들
 */

/**
 * 대시보드 스타일 계산 함수
 * @param {Object} dashboardTemplate - 대시보드 템플릿 객체
 * @returns {Object} 계산된 스타일 객체
 */
export const calculateDashboardStyle = (dashboardTemplate) => {
  // 대시보드 옵션 로깅 (성능을 위해 비활성화)
  // console.log("[Dashboard] 스타일 계산 - 대시보드 옵션:", dashboardTemplate?.dashboard_options);
  
  // 대시보드 옵션이 없으면 기본 스타일 반환
  if (!dashboardTemplate || !dashboardTemplate.dashboard_options) {
    return {
      backgroundColor: '#f5f5f5',
      padding: '20px'
    };
  }
  
  const options = dashboardTemplate.dashboard_options;

  // v1 하위호환: background_color만 있는 경우 solid로 간주
  const bg = options.background || null;
  const backgroundMode = bg?.mode || (options.background_color ? 'solid' : 'solid');
  const style = {};

  // 공용 텍스트 컬러 변수(기본값)
  style['--text-color'] = '#1f2937';

  if (backgroundMode === 'gradient' && bg?.gradient) {
    // gradient: { angleDeg, stops: [{ color, positionPercent }] }
    const angle = typeof bg.gradient.angleDeg === 'number' ? bg.gradient.angleDeg : 135;
    const stops = Array.isArray(bg.gradient.stops) && bg.gradient.stops.length > 0
      ? bg.gradient.stops
      : [
          { color: '#f0f8ff', positionPercent: 0 },
          { color: '#c8e4f1', positionPercent: 100 }
        ];
    const stopStr = stops
      .map(s => `${s.color} ${Math.max(0, Math.min(100, Number(s.positionPercent) || 0))}%`)
      .join(', ');
    style.backgroundImage = `linear-gradient(${angle}deg, ${stopStr})`;
  } else {
    // solid color (기본)
    style.backgroundColor = options.background_color || bg?.solid?.color || '#f5f5f5';
  }
  
  // 패딩 설정
  if (options.padding) {
    style.padding = `${options.padding.top || 20}px ${options.padding.right || 20}px ${options.padding.bottom || 20}px ${options.padding.left || 20}px`;
  } else {
    style.padding = '20px';
  }
  
  // console.log("[Dashboard] 계산된 스타일:", style);
  return style;
};

/**
 * 헤더 스타일 계산 함수
 * @param {Object} dashboardTemplate - 대시보드 템플릿 객체
 * @param {boolean} editMode - 편집 모드 여부
 * @returns {Object} 계산된 헤더 스타일 객체
 */
export const calculateHeaderStyle = (dashboardTemplate, editMode) => {
  // 대시보드 옵션이 없으면 기본 스타일 반환
  if (!dashboardTemplate || !dashboardTemplate.dashboard_options || !dashboardTemplate.dashboard_options.header) {
    // 편집 모드가 아닐 때
    if (!editMode) {
      // 타이틀이 비어있으면 헤더를 완전히 숨김
      if (!dashboardTemplate?.title) {
        return { display: 'none' };
      }
      // 타이틀이 있을 때는 표시
      return {
        backgroundColor: '#ffffff',
        color: '#333333',
        height: '60px',
        fontSize: '1.5rem',
        paddingLeft: '20px',
        paddingRight: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      };
    }
    return {
      backgroundColor: '#ffffff',
      color: '#333333',
      height: '60px'
    };
  }
  
  const headerOptions = dashboardTemplate.dashboard_options.header;
  
  // 헤더가 표시되지 않는 경우
  if (headerOptions.show === false) {
    return { display: 'none' };
  }
  
  // 편집 모드가 아닐 때
  if (!editMode) {
    // 타이틀이 비어있으면 헤더를 완전히 숨김
    if (!dashboardTemplate?.title) {
      return { display: 'none' };
    }
    // 타이틀이 있을 때는 표시
    return {
      height: `${headerOptions.height || 60}px`,
      backgroundColor: headerOptions.background_color || '#ffffff',
      color: headerOptions.text_color || '#333333',
      fontSize: '1.5rem',
      paddingLeft: '20px',
      paddingRight: '20px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    };
  }
  
  // 스타일 객체 생성
  const headerStyle = {
    height: `${headerOptions.height || 60}px`,
    backgroundColor: headerOptions.background_color || '#ffffff',
    color: headerOptions.text_color || '#333333'
  };
  
  console.log("[Dashboard] 계산된 헤더 스타일:", headerStyle);
  return headerStyle;
};

/**
 * 행 높이 계산 함수 - 컬럼 기반으로 정사각형 그리드 생성
 * @param {Object} dashboardTemplate - 대시보드 템플릿 객체
 * @param {number} containerWidth - 컨테이너의 실제 폭 (픽셀)
 * @returns {number} 계산된 행 높이
 */
export const calculateRowHeight = (dashboardTemplate, containerWidth = 1200) => {
  // 컬럼 수 기반으로 정사각형 그리드 생성
  const columns = dashboardTemplate?.layout?.columns || 12;
  // grid_spacing은 0도 유효값이므로 nullish 병합 사용
  const gridSpacing = dashboardTemplate?.dashboard_options?.grid_spacing ?? 10;
  
  // 각 그리드 셀의 폭 계산 (여백 제외)
  const totalSpacing = (columns - 1) * gridSpacing;
  const cellWidth = (containerWidth - totalSpacing) / columns;
  
  // 정사각형으로 만들기 위해 높이 = 폭
  return cellWidth;
}; 